import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import QrCode2OutlinedIcon from "@mui/icons-material/QrCode2Outlined";
import RuleOutlinedIcon from "@mui/icons-material/RuleOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import { doc, getDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import StudentQrTable from "../components/studentQr/StudentQrTable";
import StudentQrUploadPanel from "../components/studentQr/StudentQrUploadPanel";
import { auth, db } from "../config/firebaseConfig";
import { deleteStudentQrImage, uploadStudentQrImage } from "../services/storageService";
import {
  clearStudentQr,
  getStudentByUid,
  StudentQrError,
  subscribeToStudents,
  updateStudentQr,
} from "../services/studentService";
import {
  INVALID_QR_FILENAME_MESSAGE,
  isQrImageFile,
  parseStudentUidFromFilename,
} from "../utils/studentQr";
import {
  getAllowedRoles,
  hasPermission,
  PERMISSIONS,
} from "../utils/roleAccess";

const DUPLICATE_QUEUE_MESSAGE =
  "Duplicate UID detected in the queue. Upload order determines the final QR image.";

function createQueueItem(file) {
  const imageFile = isQrImageFile(file);
  const uid = imageFile ? parseStudentUidFromFilename(file.name) : null;

  return {
    id: `${file.name}-${file.lastModified}-${Math.random()
      .toString(36)
      .slice(2, 9)}`,
    file,
    fileName: file.name,
    uid,
    progress: 0,
    status: imageFile && uid ? "pending" : "error",
    message: !imageFile
      ? "Only image files are supported for QR upload."
      : uid
        ? ""
        : INVALID_QR_FILENAME_MESSAGE,
  };
}

function annotateDuplicateQueueEntries(items) {
  const uidCounts = items.reduce((accumulator, item) => {
    if (item.uid) {
      accumulator[item.uid] = (accumulator[item.uid] || 0) + 1;
    }
    return accumulator;
  }, {});

  return items.map((item) => {
    if (!item.uid || uidCounts[item.uid] < 2) {
      if (item.message === DUPLICATE_QUEUE_MESSAGE) {
        return { ...item, message: "" };
      }
      return item;
    }

    return {
      ...item,
      message:
        item.status === "error" && item.message
          ? item.message
          : DUPLICATE_QUEUE_MESSAGE,
    };
  });
}

function createSummaryCards(students) {
  const totalStudents = students.length;
  const linkedQr = students.filter((student) => Boolean(student.qrImageUrl)).length;
  const activeQr = students.filter((student) => student.activeqr === true).length;

  return [
    {
      label: "Students synced",
      value: totalStudents,
      icon: <QrCode2OutlinedIcon color="primary" />,
    },
    {
      label: "QR images linked",
      value: linkedQr,
      icon: <ImageOutlinedIcon color="success" />,
    },
    {
      label: "Active QR true",
      value: activeQr,
      icon: <RuleOutlinedIcon color="info" />,
    },
    {
      label: "Pending QR setup",
      value: Math.max(totalStudents - linkedQr, 0),
      icon: <WarningAmberRoundedIcon color="warning" />,
    },
  ];
}

function getReadableErrorMessage(error) {
  if (error instanceof StudentQrError) {
    return error.message;
  }

  switch (error?.code) {
    case "storage/object-not-found":
      return "QR image not found in Firebase Storage.";
    case "storage/unauthorized":
      return "You do not have permission to access Firebase Storage.";
    default:
      return error?.message || "Unexpected Firebase error. Please try again.";
  }
}

export default function StudentQrManager() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [user, setUser] = useState(null);
  const [canManageQr, setCanManageQr] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(true);
  const [queue, setQueue] = useState([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [overwriteExisting, setOverwriteExisting] = useState(true);
  const [dragActive, setDragActive] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [rowOperationState, setRowOperationState] = useState({});
  const [snackbarState, setSnackbarState] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const showSnackbar = useCallback((severity, message) => {
    setSnackbarState({
      open: true,
      severity,
      message,
    });
  }, []);

  const updateQueueItem = useCallback((queueItemId, updates) => {
    setQueue((currentItems) =>
      currentItems.map((item) =>
        item.id === queueItemId ? { ...item, ...updates } : item,
      ),
    );
  }, []);

  const clearRowOperation = useCallback((studentId) => {
    setRowOperationState((currentState) => {
      const nextState = { ...currentState };
      delete nextState[studentId];
      return nextState;
    });
  }, []);

  useEffect(() => {
    let active = true;

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!active) {
        return;
      }

      try {
        if (!currentUser) {
          setUser(null);
          setCanManageQr(false);
          return;
        }

        setUser(currentUser);
        const userReference = doc(db, "users", currentUser.uid);
        const userSnapshot = await getDoc(userReference);

        if (!active) {
          return;
        }

        setCanManageQr(
          userSnapshot.exists() &&
            hasPermission(userSnapshot.data(), PERMISSIONS.STUDENT_QR_MANAGER),
        );
      } catch (error) {
        if (active) {
          showSnackbar("error", getReadableErrorMessage(error));
          setCanManageQr(false);
        }
      } finally {
        if (active) {
          setAuthLoading(false);
        }
      }
    });

    return () => {
      active = false;
      unsubscribe();
    };
  }, [showSnackbar]);

  useEffect(() => {
    if (authLoading) {
      return undefined;
    }

    if (!user || !canManageQr) {
      setStudentsLoading(false);
      return undefined;
    }

    setStudentsLoading(true);

    const unsubscribe = subscribeToStudents(
      (studentRecords) => {
        setStudents(studentRecords);
        setStudentsLoading(false);
      },
      (error) => {
        setStudentsLoading(false);
        showSnackbar("error", getReadableErrorMessage(error));
      },
    );

    return () => unsubscribe();
  }, [authLoading, canManageQr, showSnackbar, user]);

  useEffect(() => {
    setPage(0);
  }, [searchText, statusFilter]);

  const summaryCards = useMemo(() => createSummaryCards(students), [students]);

  const filteredStudents = useMemo(() => {
    const normalizedQuery = searchText.trim().toLowerCase();

    return students.filter((student) => {
      const matchesSearch =
        !normalizedQuery ||
        String(student.name || "").toLowerCase().includes(normalizedQuery) ||
        String(student.uid || "").toLowerCase().includes(normalizedQuery);

      if (!matchesSearch) {
        return false;
      }

      switch (statusFilter) {
        case "withQr":
          return Boolean(student.qrImageUrl);
        case "withoutQr":
          return !student.qrImageUrl;
        case "active":
          return student.activeqr === true;
        case "inactive":
          return student.activeqr !== true;
        default:
          return true;
      }
    });
  }, [searchText, statusFilter, students]);

  const paginatedStudents = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredStudents.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredStudents, page, rowsPerPage]);

  useEffect(() => {
    const maxPage = Math.max(Math.ceil(filteredStudents.length / rowsPerPage) - 1, 0);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [filteredStudents.length, page, rowsPerPage]);

  const handleQueueFiles = useCallback(
    (fileList) => {
      const files = Array.from(fileList || []);

      if (!files.length) {
        return;
      }

      const newQueueItems = files.map(createQueueItem);

      setQueue((currentItems) =>
        annotateDuplicateQueueEntries([...currentItems, ...newQueueItems]),
      );

      showSnackbar("success", `${files.length} file(s) added to the upload queue.`);
    },
    [showSnackbar],
  );

  const handleBulkUpload = useCallback(async () => {
    const pendingItems = queue.filter((item) => item.status === "pending");

    if (!pendingItems.length) {
      showSnackbar("warning", "No pending QR uploads in the queue.");
      return;
    }

    setBulkUploading(true);
    let successCount = 0;
    let skippedCount = 0;
    let errorCount = 0;

    for (const item of pendingItems) {
      try {
        updateQueueItem(item.id, {
          status: "uploading",
          progress: 0,
          message: item.message === DUPLICATE_QUEUE_MESSAGE ? item.message : "",
        });

        const student = await getStudentByUid(item.uid);

        if (student.qrImageUrl && !overwriteExisting) {
          skippedCount += 1;
          updateQueueItem(item.id, {
            status: "skipped",
            progress: 0,
            message:
              "QR image already exists for this student. Enable overwrite to replace it.",
          });
          continue;
        }

        const qrImageUrl = await uploadStudentQrImage({
          uid: item.uid,
          file: item.file,
          onProgress: (progress) => {
            updateQueueItem(item.id, { progress });
          },
        });

        const { activeqr } = await updateStudentQr({
          studentId: student.id,
          studentName: student.name,
          qrImageUrl,
        });

        successCount += 1;
        updateQueueItem(item.id, {
          status: "success",
          progress: 100,
          message: activeqr
            ? `Linked to ${student.name || item.uid}. activeqr set to true.`
            : `Linked to ${student.name || item.uid}. activeqr set to false by name rule.`,
        });
      } catch (error) {
        errorCount += 1;
        updateQueueItem(item.id, {
          status: "error",
          progress: 0,
          message: getReadableErrorMessage(error),
        });
      }
    }

    setBulkUploading(false);

    showSnackbar(
      errorCount > 0 ? "warning" : "success",
      `Bulk upload finished. Success: ${successCount}, Skipped: ${skippedCount}, Errors: ${errorCount}.`,
    );
  }, [overwriteExisting, queue, showSnackbar, updateQueueItem]);

  const handleStudentUpload = useCallback(
    async (student, file) => {
      if (!file) {
        return;
      }

      if (!isQrImageFile(file)) {
        showSnackbar("error", "Only image files are supported for QR upload.");
        return;
      }

      if (student.qrImageUrl && !overwriteExisting) {
        showSnackbar(
          "warning",
          "Overwrite is disabled. Enable it to replace an existing QR image.",
        );
        return;
      }

      setRowOperationState((currentState) => ({
        ...currentState,
        [student.id]: {
          action: "upload",
          loading: true,
          progress: 0,
        },
      }));

      try {
        const qrImageUrl = await uploadStudentQrImage({
          uid: student.uid,
          file,
          onProgress: (progress) => {
            setRowOperationState((currentState) => ({
              ...currentState,
              [student.id]: {
                action: "upload",
                loading: true,
                progress,
              },
            }));
          },
        });

        const { activeqr } = await updateStudentQr({
          studentId: student.id,
          studentName: student.name,
          qrImageUrl,
        });

        showSnackbar(
          "success",
          activeqr
            ? `QR image saved for ${student.name || student.uid}.`
            : `QR image saved for ${student.name || student.uid}; activeqr remained false by rule.`,
        );
      } catch (error) {
        showSnackbar("error", getReadableErrorMessage(error));
      } finally {
        clearRowOperation(student.id);
      }
    },
    [clearRowOperation, overwriteExisting, showSnackbar],
  );

  const handleDeleteStudentQr = useCallback(
    async (student) => {
      if (!student?.qrImageUrl) {
        showSnackbar("warning", "This student does not have a QR image to delete.");
        return;
      }

      const confirmed = window.confirm(
        `Delete the QR image for ${student.name || student.uid}?`,
      );

      if (!confirmed) {
        return;
      }

      setRowOperationState((currentState) => ({
        ...currentState,
        [student.id]: {
          action: "delete",
          loading: true,
          progress: 0,
        },
      }));

      try {
        await deleteStudentQrImage({
          qrImageUrl: student.qrImageUrl,
          uid: student.uid,
        });
        await clearStudentQr({ studentId: student.id });

        showSnackbar("success", `QR image deleted for ${student.name || student.uid}.`);
      } catch (error) {
        showSnackbar("error", getReadableErrorMessage(error));
      } finally {
        clearRowOperation(student.id);
      }
    },
    [clearRowOperation, showSnackbar],
  );

  const handleDragEnter = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
  }, []);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      setDragActive(false);
      handleQueueFiles(event.dataTransfer.files);
    },
    [handleQueueFiles],
  );

  if (authLoading || studentsLoading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "grey.50",
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <CircularProgress size={24} />
          <Typography color="text.secondary">Loading QR manager...</Typography>
        </Stack>
      </Box>
    );
  }

  if (!user || !canManageQr) {
    const allowedRoles = getAllowedRoles(PERMISSIONS.STUDENT_QR_MANAGER).join(
      ", ",
    );

    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "grey.50",
          px: 2,
        }}
      >
        <Card sx={{ maxWidth: 480, borderRadius: 4 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" fontWeight={700}>
              Access required
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1.5, mb: 3 }}>
              This module writes to Firebase Firestore and Storage. Allowed
              roles: {allowedRoles || "admin"}.
            </Typography>
            <Button variant="contained" onClick={() => navigate("/login")}>
              Login
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f4f7fb", py: { xs: 3, md: 4 } }}>
      <Container maxWidth="xl">
        <Stack spacing={3}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
          >
            <Box>
              <Typography variant="h4" fontWeight={800}>
                Student QR Manager
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                Upload, replace, and delete QR images in Firebase Storage, then
                sync `qrImageUrl` and `activeqr` in the `students` collection.
              </Typography>
            </Box>

            <Button
              variant="outlined"
              startIcon={<ArrowBackRoundedIcon />}
              onClick={() => navigate("/admin-dashboard")}
              sx={{ borderRadius: 999 }}
            >
              Back to Dashboard
            </Button>
          </Stack>

          <Grid container spacing={2.5}>
            {summaryCards.map((card) => (
              <Grid item xs={12} sm={6} lg={3} key={card.label}>
                <Card sx={{ borderRadius: 4, boxShadow: "none" }}>
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {card.label}
                        </Typography>
                        <Typography variant="h4" fontWeight={800} sx={{ mt: 1 }}>
                          {card.value}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          display: "grid",
                          placeItems: "center",
                          borderRadius: 3,
                          bgcolor: "grey.100",
                        }}
                      >
                        {card.icon}
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <StudentQrUploadPanel
            bulkUploading={bulkUploading}
            dragActive={dragActive}
            fileInputRef={fileInputRef}
            overwriteExisting={overwriteExisting}
            queue={queue}
            onOpenFilePicker={() => fileInputRef.current?.click()}
            onFilesSelected={handleQueueFiles}
            onUploadPending={handleBulkUpload}
            onClearQueue={() => setQueue([])}
            onRemoveQueueItem={(queueItemId) =>
              setQueue((currentItems) =>
                annotateDuplicateQueueEntries(
                  currentItems.filter((item) => item.id !== queueItemId),
                ),
              )
            }
            onToggleOverwriteExisting={setOverwriteExisting}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          />

          <StudentQrTable
            loading={studentsLoading}
            onDeleteStudentQr={handleDeleteStudentQr}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(Number(event.target.value));
              setPage(0);
            }}
            onSearchTextChange={setSearchText}
            onStatusFilterChange={setStatusFilter}
            onUploadForStudent={handleStudentUpload}
            page={page}
            rowsPerPage={rowsPerPage}
            rowOperationState={rowOperationState}
            searchText={searchText}
            statusFilter={statusFilter}
            students={paginatedStudents}
            totalCount={filteredStudents.length}
          />
        </Stack>
      </Container>

      <Snackbar
        open={snackbarState.open}
        autoHideDuration={4500}
        onClose={(_, reason) => {
          if (reason === "clickaway") {
            return;
          }
          setSnackbarState((currentState) => ({
            ...currentState,
            open: false,
          }));
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snackbarState.severity} variant="filled">
          {snackbarState.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
